import type { ArticleMeta, ArticleModule } from '../types/article';
import type { Profile } from '../types/profile';
import { canViewProfileContent } from './permissions';

const modules = import.meta.glob<ArticleModule>('../content/**/*.mdx', { eager: true });
const rawSources = import.meta.glob('../content/**/*.mdx', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, unknown>;

export interface ArticleRecord {
  path: string;
  meta: ArticleMeta;
  Component: ArticleModule['default'];
  bodyText: string;
}

function resolveRawSource(source: unknown) {
  if (typeof source === 'string') return source;
  if (source && typeof source === 'object' && 'default' in source) {
    const value = (source as { default?: unknown }).default;
    if (typeof value === 'string') return value;
  }
  return '';
}

function bodyFromSource(source: unknown) {
  return resolveRawSource(source)
    .replace(/export const meta = \{[\s\S]*?\n\}\s*/m, '')
    .replace(/[>#*_`[\]()-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export const articles: ArticleRecord[] = Object.entries(modules)
  .map(([path, module]) => ({
    path,
    meta: module.meta,
    Component: module.default,
    bodyText: bodyFromSource(rawSources[path]),
  }))
  .filter((article) => article.meta.status !== 'archived')
  .sort((a, b) => a.meta.order - b.meta.order);

export function getArticleById(id: string) {
  return articles.find((article) => article.meta.id === id);
}

export function getVisibleArticles(profile: Profile) {
  return articles.filter(
    (article) => article.meta.status === 'published' && canViewProfileContent(profile, article.meta.profile),
  );
}

export function getArticleByRoute(profile: Profile, category: string, slug: string) {
  return getVisibleArticles(profile).find(
    (article) => article.meta.category === category && article.meta.slug === slug,
  );
}

export function getRelatedArticles(profile: Profile, ids: string[] = []) {
  return ids
    .map((id) => getArticleById(id))
    .filter((article): article is ArticleRecord => Boolean(
      article && article.meta.status === 'published' && canViewProfileContent(profile, article.meta.profile),
    ));
}

export function getArticleNeighbors(profile: Profile, articleId: string) {
  const current = getArticleById(articleId);
  if (!current) return {};

  const sequence = articles.filter(
    (article) => article.meta.status === 'published'
      && article.meta.profile === current.meta.profile
      && canViewProfileContent(profile, article.meta.profile),
  );
  const index = sequence.findIndex((article) => article.meta.id === articleId);

  const explicitPrevious = current.meta.previous ? getArticleById(current.meta.previous) : undefined;
  const explicitNext = current.meta.next ? getArticleById(current.meta.next) : undefined;

  const allowed = (article?: ArticleRecord) => article
    && article.meta.status === 'published'
    && canViewProfileContent(profile, article.meta.profile)
      ? article
      : undefined;

  return {
    previous: allowed(explicitPrevious) ?? (index > 0 ? sequence[index - 1] : undefined),
    next: allowed(explicitNext) ?? (index >= 0 && index < sequence.length - 1 ? sequence[index + 1] : undefined),
  };
}
